import { formatInstant } from '@/lib/utils/date';
import { formatGa, gaDays } from './calculations';
import { activeEvents, displayPayload } from './projection';
import { prenatalDefinition, type FlowsheetColumn, type FlowsheetDefinition } from './definitions';
import type { ClinicalEvent, ClinicalSubject } from './types';
export function rowsForDefinition(events: ClinicalEvent[], definition: FlowsheetDefinition) {
  if (definition.rowStrategy === 'PER_TIMESTAMP_BUCKET' && (!definition.bucketSeconds || definition.bucketSeconds < 1))
    throw new Error('A positive bucket width is required');
  const rows = new Map<string, ClinicalEvent[]>();
  for (const e of activeEvents(events)) {
    const key =
      definition.rowStrategy === 'PER_ENCOUNTER'
        ? e.encounter_id
        : definition.rowStrategy === 'PER_EVENT'
          ? e.id
          : `${e.encounter_id}/${Math.floor(Date.parse(e.occurred_at) / (definition.bucketSeconds! * 1000))}`;
    rows.set(key, [...(rows.get(key) ?? []), e]);
  }
  return [...rows.entries()]
    .map(([id, events]) => ({
      id,
      events: events.sort((a, b) => Date.parse(a.occurred_at) - Date.parse(b.occurred_at) || a.sequence - b.sequence),
    }))
    .sort(
      (a, b) =>
        (Date.parse(a.events[0].occurred_at) - Date.parse(b.events[0].occurred_at)) *
        (definition.sort === 'ASC' ? 1 : -1),
    );
}
export function cellValue(event: ClinicalEvent, column: FlowsheetColumn, baseline?: number | null): string {
  const p = event.payload;
  if (column.key === 'weight_gain')
    return baseline == null || typeof p.grams !== 'number'
      ? '—'
      : `${((p.grams - baseline) / 453.59237).toFixed(1)} lb / ${((p.grams - baseline) / 1000).toFixed(2)} kg`;
  if (column.key === 'rhig')
    return p.rhig_given === undefined ? '—' : `${p.rhig_given ? 'Yes' : 'No'}${p.rhig_date ? ' · ' + p.rhig_date : ''}`;
  if (typeof p[column.path] === 'boolean') return p[column.path] ? 'Yes' : 'No';
  if (event.event_type === 'VITALS.WEIGHT' && typeof p.grams === 'number')
    return `${(p.grams / 453.59237).toFixed(1)} lb / ${(p.grams / 1000).toFixed(2)} kg`;
  if (column.format === 'bp') return `${p.systolic}/${p.diastolic}`;
  if (event.event_type === 'LAB.URINE_DIP') return displayPayload(p);
  if (event.event_type === 'PRENATAL.FHT')
    return [p.bpm !== undefined ? String(p.bpm) : '', p.low_bpm !== undefined ? `${p.low_bpm}–${p.high_bpm}` : '']
      .filter(Boolean)
      .join('; ');
  if (event.event_type === 'PRENATAL.FUNDAL_HEIGHT')
    return [
      p.cm !== undefined ? `${p.cm} cm` : '',
      p.landmark !== undefined ? [p.fingers, p.landmark].filter((v) => v !== undefined).join(' ') : '',
    ]
      .filter(Boolean)
      .join('; ');
  if (event.event_type === 'VITALS.TEMP') return `${p.value_c} ${p.method ?? ''}`.trim();
  return p[column.path] === undefined ? '—' : String(p[column.path]);
}
export function renderFlowsheet(
  events: ClinicalEvent[],
  subjects: ClinicalSubject[],
  timeZone: string,
  edd: string | null = null,
  definition = prenatalDefinition,
  numbersOnly = false,
  baseline?: number | null,
) {
  const columns = ['MOTHER', 'FETUS', 'NEWBORN'].flatMap((scope) =>
    subjects
      .filter((s) => s.subject_type === scope)
      .flatMap((s) =>
        definition.columns
          .filter((c) => c.subject === scope)
          .map((column) => ({
            column,
            subjectId: s.id,
            label: scope === 'MOTHER' ? column.label : `${s.label} · ${column.label}`,
          })),
      ),
  );
  const selected = numbersOnly ? events.filter((e) => !e.event_type.startsWith('NOTE.')) : events;
  const rows = rowsForDefinition(selected, definition).map((row) => ({
    ...row,
    cells: columns.map(({ column, subjectId }) =>
      row.events.filter(
        (e) =>
          e.event_type === column.eventType &&
          e.subject_id === subjectId &&
          (column.schemaVersion === undefined || e.schema_version === column.schemaVersion),
      ),
    ),
    date: formatInstant(row.events[0].occurred_at, 'MMM d, yyyy HH:mm', timeZone),
    ga: formatGa(gaDays(formatInstant(row.events[0].occurred_at, 'yyyy-MM-dd', timeZone), edd)),
  }));
  return { columns, rows };
}
export function renderedRows(
  events: ClinicalEvent[],
  subjects: ClinicalSubject[],
  timeZone: string,
  edd: string | null = null,
  definition = prenatalDefinition,
  numbersOnly = false,
  baseline?: number | null,
) {
  const { columns, rows } = renderFlowsheet(events, subjects, timeZone, edd, definition, numbersOnly);
  return [
    ['Visit observations', 'GA', ...columns.map((c) => c.label), 'Recorded by', 'Signature status'],
    ...rows.map((r) => [
      r.date,
      r.ga,
      ...r.cells.map(
        (es, i) =>
          es
            .map(
              (e) =>
                `${cellValue(e, columns[i].column, baseline)} [${formatInstant(e.occurred_at, 'HH:mm', timeZone)} · ${e.author_initials}]`,
            )
            .join('\n') || '—',
      ),
      [...new Set(r.events.map((e) => e.author_initials))].join(', '),
      `${r.events.filter((e) => e.signed).length} of ${r.events.length} observations signed`,
    ]),
  ];
}
